import React, { useState, useMemo, useEffect } from 'react';
import { 
  Card, 
  List, 
  Avatar, 
  Space, 
  Tag, 
  Typography, 
  Button, 
  Tooltip, 
  Badge,
  Divider,
  Empty,
  Modal,
  Form,
  Input,
  message,
  Spin
} from 'antd';
import {
  EnvironmentOutlined,
  UserOutlined,
  PhoneOutlined,
  MailOutlined,
  InfoCircleOutlined,
  MessageOutlined,
  CloseOutlined,
  SendOutlined,
  SearchOutlined,
  ClearOutlined
} from '@ant-design/icons';
import { useAuth } from '../../contexts/AuthContext';
import { createIncidentLog } from '../../utils/activityLogger';
import { getOrgSiteTypes } from '../../services/siteService';
import SMSSegmentIndicator from '../SMSSegmentIndicator';

const { Title, Text } = Typography;
const { TextArea } = Input;

/**
 * SiteListPopup - Displays sites detected within a circle
 * Phase 1: Auto-detection and display functionality
 * Phase 2: SMS Mass Communication functionality
 */
const SiteListPopup = ({ 
  sites = [], 
  circle = null,
  onClose = null,
  className = ''
}) => {
  const { currentUser } = useAuth();
  const [showContactModal, setShowContactModal] = useState(false);
  const [smsForm] = Form.useForm();
  const [sendingSMS, setSendingSMS] = useState(false);
  const [smsResults, setSmsResults] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [orgSiteTypes, setOrgSiteTypes] = useState([]);

  // Load organization site types
  useEffect(() => {
    const loadOrgSiteTypes = async () => {
      if (currentUser?.orgId) {
        try {
          const orgTypes = await getOrgSiteTypes(currentUser.orgId);
          setOrgSiteTypes(orgTypes);
        } catch (error) {
          console.error('Error loading org site types:', error);
        }
      }
    };
    
    loadOrgSiteTypes();
  }, [currentUser?.orgId]);

  // Clear search function
  const clearSearch = () => {
    setSearchTerm('');
  };

  // Create dynamic site type colors from organization's registered types
  const SITE_TYPE_COLORS = useMemo(() => {
    const colors = {};
    
    // Convert org site types to colors object
    orgSiteTypes.forEach(type => {
      colors[type.name] = type.color;
    });
    
    // Fallback for unregistered types
    const fallbackColors = {
    other: '#95a5a6'
  };
    
    return { ...colors, ...fallbackColors };
  }, [orgSiteTypes]);

  // Status colors - Crisis Management focused
  const STATUS_COLORS = {
    active: '#27ae60',        // Green - operational
    degraded: '#f39c12',      // Orange - warning
    evacuating: '#e74c3c',    // Red - urgent action
    evacuated: '#9b59b6',     // Purple - completed evacuation
    closed_and_secure: '#3498db', // Blue - secure
    unknown: '#95a5a6'        // Gray - default/no info
  };

  // Filter sites based on search term
  const filteredSites = useMemo(() => {
    if (!searchTerm.trim()) {
      return sites;
    }

    const search = searchTerm.toLowerCase().trim();
    return sites.filter(site => 
      // Site name
      site.site_name?.toLowerCase().includes(search) ||
      // Site type
      site.site_type?.toLowerCase().includes(search) ||
      // Manager name
      site.site_manager_name?.toLowerCase().includes(search) ||
      // Address fields
      site.address_line_1?.toLowerCase().includes(search) ||
      site.address_line_2?.toLowerCase().includes(search) ||
      site.suburb?.toLowerCase().includes(search) ||
      site.state?.toLowerCase().includes(search) ||
      site.postcode?.toLowerCase().includes(search) ||
      // Status
      site.status?.toLowerCase().includes(search) ||
      // Contact info
      site.site_manager_mobile?.toLowerCase().includes(search) ||
      site.site_manager_email?.toLowerCase().includes(search)
    );
  }, [sites, searchTerm]);

  // Site statistics (based on filtered sites)
  const siteStats = useMemo(() => {
    const stats = {
      total: filteredSites.length,
      originalTotal: sites.length,
      byType: {},
      byStatus: {},
      withContacts: filteredSites.filter(site => 
        site.site_manager_mobile && site.site_manager_mobile.trim()
      ).length
    };

    filteredSites.forEach(site => {
      // By type
      stats.byType[site.site_type] = (stats.byType[site.site_type] || 0) + 1;
      
      // By status
      stats.byStatus[site.status || 'unknown'] = (stats.byStatus[site.status || 'unknown'] || 0) + 1;
    });

    return stats;
  }, [filteredSites, sites]);

  // Get filtered sites with valid mobile numbers
  const sitesWithContacts = useMemo(() => {
    return filteredSites.filter(site => 
      site.site_manager_mobile && 
      site.site_manager_mobile.trim() &&
      site.site_manager_name
    );
  }, [filteredSites]);

  // SMS Templates
  const SMS_TEMPLATES = [
    {
      id: 'emergency_alert',
      name: 'Emergency Alert',
      message: 'EMERGENCY ALERT: This is an urgent notification regarding your site. Please respond immediately to confirm your safety and site status. Reply with your current situation.'
    },
    {
      id: 'safety_check',
      name: 'Safety Check-in',
      message: 'This is a site security/safety check from {organisation_name} Crisis Management. Please confirm your site\'s operational status:\n1 = Active\n2 = Degraded\n3 = Evacuating\n4 = Evacuated\n5 = Closed and Secure'
    },
    {
      id: 'incident_notification',
      name: 'Incident Notification',
      message: 'Incident notification: An incident has been reported in your area. Please review your site security and report any unusual activity immediately.'
    },
    {
      id: 'evacuation_alert',
      name: 'Evacuation Alert',
      message: 'EVACUATION ALERT: Please evacuate your site immediately and proceed to the designated safe area. Confirm evacuation completion by replying to this message.'
    }
  ];

  // Log mass SMS activity to incident logs
  const logMassSMSActivity = async (results, smsMessage) => {
    try {
      // Only log if we're in a crisis context
      const urlParams = new URLSearchParams(window.location.search);
      const incidentId = urlParams.get('id') || window.location.pathname.split('/').pop();
      
      if (!incidentId || incidentId === 'map') {
        console.log('📱 Mass SMS sent outside incident context - not logging to incident logs');
        return;
      }

      const successCount = results.filter(r => r.status === 'success').length;
      const errorCount = results.filter(r => r.status === 'error').length;
      const totalCount = results.length;

      // Create summary for activity log
      const recipientList = results
        .filter(r => r.status === 'success')
        .map(r => `${r.manager} (${r.site})`)
        .join(', ');

      const failedList = results
        .filter(r => r.status === 'error')
        .map(r => `${r.manager} (${r.site})`)
        .join(', ');

      let details = `Mass SMS sent to ${totalCount} site managers within circle area. `;
      details += `${successCount} successful, ${errorCount} failed. `;
      details += `Message: "${smsMessage.substring(0, 100)}${smsMessage.length > 100 ? '...' : ''}"`;
      
      if (successCount > 0) {
        details += ` | Successful recipients: ${recipientList}`;
      }
      
      if (errorCount > 0) {
        details += ` | Failed recipients: ${failedList}`;
      }

      await createIncidentLog({
        incidentId: incidentId,
        orgId: currentUser.orgId,
        action: 'mass_sms_communication',
        details: details,
        userId: currentUser.uid,
        userEmail: currentUser.email,
        userFullName: currentUser.displayName || currentUser.email,
        priority: errorCount > 0 ? 'high' : 'normal',
        metadata: {
          smsType: 'mass',
          totalRecipients: totalCount,
          successfulSends: successCount,
          failedSends: errorCount,
          messageLength: smsMessage.length,
          circleRadius: circle ? (circle.getRadius() / 1000).toFixed(2) + 'km' : 'unknown',
          communicationType: 'sms',
          results: results.map(r => ({
            site: r.site,
            manager: r.manager,
            phone: r.phone,
            status: r.status,
            sid: r.sid || null,
            error: r.error || null
          }))
        }
      });

      console.log(`📋 Mass SMS activity logged to incident ${incidentId}: ${successCount}/${totalCount} successful`);
    } catch (logError) {
      console.error('❌ Failed to log mass SMS activity:', logError);
      // Don't throw - SMS logging failure shouldn't break SMS functionality
    }
  };

  // Handle SMS sending
  const handleSendSMS = async (values) => {
    setSendingSMS(true);
    setSmsResults([]);
    
    try {
      const { message: smsMessage } = values;
      
      if (!smsMessage || !smsMessage.trim()) {
        message.error('Please enter a message to send');
        return;
      }

      if (sitesWithContacts.length === 0) {
        message.error('No sites with valid contact information found');
        return;
      }

      // Extract incidentId from URL for SMS tracking
      const urlParams = new URLSearchParams(window.location.search);
      const incidentId = urlParams.get('id') || window.location.pathname.split('/').pop();

      // Replace {organisation_name} template variable
      let processedMessage = smsMessage;
      if (smsMessage.includes('{organisation_name}')) {
        try {
          // Fetch organization data for name replacement
          const { getDoc, doc } = await import('firebase/firestore');
          const { db } = await import('../../config/firebase');
          const orgRef = doc(db, 'c3_organisations', currentUser.orgId);
          const orgDoc = await getDoc(orgRef);
          
          if (orgDoc.exists()) {
            const orgData = orgDoc.data();
            const orgName = orgData.name || orgData.companyName || 'Your Organization';
            processedMessage = smsMessage.replace(/{organisation_name}/g, orgName);
            console.log(`🏢 Replaced organization name in mass SMS: ${orgName}`);
          }
        } catch (error) {
          console.warn('⚠️ Could not fetch organization name for template replacement:', error);
          processedMessage = smsMessage.replace(/{organisation_name}/g, 'Your Organization');
        }
      }

      console.log('📱 Starting mass SMS send to', sitesWithContacts.length, 'sites');
      
      const results = [];
      
      // Send SMS to each site manager
      for (const site of sitesWithContacts) {
        try {
          console.log(`📱 Sending SMS to ${site.site_manager_name} at ${site.site_manager_mobile}`);
          
          const response = await fetch('https://australia-southeast2-crisiscompass-prod.cloudfunctions.net/sendSMS', {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
            },
            body: JSON.stringify({
              to: site.site_manager_mobile,
              message: processedMessage,
              orgId: currentUser.orgId,
              userId: currentUser.uid,
              incidentId: (incidentId && incidentId !== 'map') ? incidentId : null
            })
          });

          const result = await response.json();
          
          if (response.ok) {
            results.push({
              site: site.site_name,
              manager: site.site_manager_name,
              phone: site.site_manager_mobile,
              status: 'success',
              sid: result.sid,
              timestamp: result.timestamp
            });
            console.log(`✅ SMS sent successfully to ${site.site_manager_name}`);
          } else {
            results.push({
              site: site.site_name,
              manager: site.site_manager_name,
              phone: site.site_manager_mobile,
              status: 'error',
              error: result.error || 'Unknown error'
            });
            console.error(`❌ SMS failed for ${site.site_manager_name}:`, result.error);
          }
        } catch (error) {
          results.push({
            site: site.site_name,
            manager: site.site_manager_name,
            phone: site.site_manager_mobile,
            status: 'error',
            error: error.message
          });
          console.error(`❌ SMS error for ${site.site_manager_name}:`, error);
        }

        // Small delay between SMS sends to avoid rate limiting
        await new Promise(resolve => setTimeout(resolve, 200));
      }

      setSmsResults(results);
      
      const successCount = results.filter(r => r.status === 'success').length;
      const errorCount = results.filter(r => r.status === 'error').length;
      
      // Log the mass SMS activity
      await logMassSMSActivity(results, smsMessage);
      
      if (successCount > 0) {
        message.success(`Successfully sent ${successCount} SMS messages`);
      }
      
      if (errorCount > 0) {
        message.warning(`${errorCount} SMS messages failed to send`);
      }

      // Clear form if all successful
      if (errorCount === 0) {
        smsForm.resetFields();
      }

    } catch (error) {
      console.error('❌ Mass SMS error:', error);
      message.error('Failed to send SMS messages: ' + error.message);
    } finally {
      setSendingSMS(false);
    }
  };

  // Handle modal close
  const handleModalClose = () => {
    setShowContactModal(false);
    smsForm.resetFields();
    setSmsResults([]);
  };

  // Apply SMS template
  const applyTemplate = async (template) => {
    let processedMessage = template.message;
    
    // Replace organization name in template display
    if (template.message.includes('{organisation_name}')) {
      try {
        const { getDoc, doc } = await import('firebase/firestore');
        const { db } = await import('../../config/firebase');
        const orgRef = doc(db, 'c3_organisations', currentUser.orgId);
        const orgDoc = await getDoc(orgRef);
        
        if (orgDoc.exists()) {
          const orgData = orgDoc.data();
          const orgName = orgData.name || orgData.companyName || 'Your Organization';
          processedMessage = template.message.replace(/{organisation_name}/g, orgName);
        }
      } catch (error) {
        console.warn('⚠️ Could not fetch organization name for template display:', error);
        processedMessage = template.message.replace(/{organisation_name}/g, 'Your Organization');
      }
    }
    
    smsForm.setFieldsValue({ message: processedMessage });
  };

  // Get site avatar icon
  const getSiteAvatar = (site) => {
    const color = SITE_TYPE_COLORS[site.site_type] || SITE_TYPE_COLORS.other;
    return (
      <Avatar 
        style={{ 
          backgroundColor: color, 
          color: 'white',
          fontSize: '12px',
          fontWeight: 'bold'
        }}
        size="small"
      >
        {site.site_type?.charAt(0).toUpperCase() || 'S'}
      </Avatar>
    );
  };

  // Format address for display
  const formatAddress = (site) => {
    const parts = [
      site.address_line_1,
      site.suburb,
      site.state,
      site.postcode
    ].filter(Boolean);
    return parts.join(', ');
  };

  // Calculate circle radius for display
  const getCircleRadius = () => {
    if (!circle) return null;
    const radiusMeters = circle.getRadius();
    const radiusKm = (radiusMeters / 1000).toFixed(2);
    return radiusKm;
  };

  // Render site item
  const renderSiteItem = (site) => {
    const hasValidContact = site.site_manager_mobile && site.site_manager_mobile.trim();
    
    return (
      <List.Item
        key={site.id}
        className="site-list-item"
        style={{ 
          padding: '12px',
          borderRadius: '8px',
          marginBottom: '8px',
          backgroundColor: '#fafafa',
          border: '1px solid #f0f0f0'
        }}
      >
        <List.Item.Meta
          avatar={getSiteAvatar(site)}
          title={
            <Space>
              <Text strong style={{ color: '#39325b' }}>
                {site.site_name}
              </Text>
              <Tag 
                color={SITE_TYPE_COLORS[site.site_type] || SITE_TYPE_COLORS.other}
                size="small"
              >
                {site.site_type?.charAt(0).toUpperCase() + site.site_type?.slice(1)}
              </Tag>
              {site.status && (
                <Tag 
                  color={STATUS_COLORS[site.status] || STATUS_COLORS.active}
                  size="small"
                >
                  {site.status.charAt(0).toUpperCase() + site.status.slice(1)}
                </Tag>
              )}
            </Space>
          }
          description={
            <div>
              <div className="mb-1">
                <EnvironmentOutlined className="me-1 text-muted" />
                <Text type="secondary" style={{ fontSize: '12px' }}>
                  {formatAddress(site)}
                </Text>
              </div>
              
              {site.region && (
                <div className="mb-1">
                  <InfoCircleOutlined className="me-1 text-muted" />
                  <Text type="secondary" style={{ fontSize: '12px' }}>
                    Region: {site.region}
                  </Text>
                </div>
              )}
              
              <div className="d-flex align-items-center gap-2 mt-2">
                <UserOutlined className="text-muted" style={{ fontSize: '12px' }} />
                <Text style={{ fontSize: '12px' }}>
                  {site.site_manager_name}
                </Text>
                
                {hasValidContact ? (
                  <Space size="small">
                    <Tooltip title={`Call ${site.site_manager_mobile}`}>
                      <Button
                        type="link"
                        size="small"
                        icon={<PhoneOutlined />}
                        href={`tel:${site.site_manager_mobile}`}
                        style={{ padding: '0', height: 'auto', color: '#27ae60' }}
                      />
                    </Tooltip>
                    
                    {site.site_manager_email && (
                      <Tooltip title={`Email ${site.site_manager_email}`}>
                        <Button
                          type="link"
                          size="small"
                          icon={<MailOutlined />}
                          href={`mailto:${site.site_manager_email}`}
                          style={{ padding: '0', height: 'auto', color: '#3498db' }}
                        />
                      </Tooltip>
                    )}
                  </Space>
                ) : (
                  <Text type="secondary" style={{ fontSize: '11px' }}>
                    No contact info
                  </Text>
                )}
              </div>
            </div>
          }
        />
      </List.Item>
    );
  };

  const renderStats = () => (
    <div className="mb-3 p-3" style={{ backgroundColor: '#f8f9fa', borderRadius: '8px' }}>
      <Space size="large" wrap>
        <Badge count={siteStats.total} color="#39325b">
          <Text strong>Total Sites</Text>
        </Badge>
        
        <Badge count={siteStats.withContacts} color="#27ae60">
          <Text strong>With Contacts</Text>
        </Badge>

        {circle && (
          <div>
            <Text strong>Radius: </Text>
            <Text>{getCircleRadius()} km</Text>
          </div>
        )}
      </Space>
    </div>
  );

  if (sites.length === 0) {
    return (
      <Card
        title={
          <Space>
            <Title level={5} style={{ margin: 0, color: '#39325b' }}>
              Sites in Circle
            </Title>
            {onClose && (
              <Button
                type="text"
                size="small"
                icon={<CloseOutlined />}
                onClick={onClose}
                style={{ marginLeft: 'auto' }}
              />
            )}
          </Space>
        }
        className={className}
        style={{ 
          borderRadius: '12px',
          border: '1px solid #e8e8e8',
          boxShadow: '0 4px 12px rgba(0,0,0,0.1)'
        }}
      >
        <Empty
          description="No sites found in this area"
          image={Empty.PRESENTED_IMAGE_SIMPLE}
        />
      </Card>
    );
  }

  return (
    <>
      <Card
        title={
          <Space style={{ width: '100%', justifyContent: 'space-between' }}>
            <Title level={5} style={{ margin: 0, color: '#39325b' }}>
              Sites in Circle ({searchTerm ? `${siteStats.total}/${siteStats.originalTotal}` : sites.length})
            </Title>
            {onClose && (
              <Button
                type="text"
                size="small"
                icon={<CloseOutlined />}
                onClick={onClose}
              />
            )}
          </Space>
        }
        className={className}
        style={{ 
          borderRadius: '12px',
          border: '1px solid #e8e8e8',
          boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
          maxHeight: '650px', // 🎯 CRITICAL FIX: Increased from 520px to fix bottom border cutoff
          overflow: 'auto' // 🎯 CRITICAL FIX: Changed from 'hidden' to 'auto' for proper scrolling
        }}
      >
        {/* Search Input */}
        <div style={{ marginBottom: '12px' }}>
          <Input
            placeholder="Search sites by name, type, manager, address, status..."
            prefix={<SearchOutlined />}
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            allowClear
            size="small"
            suffix={
              searchTerm && (
                <Text type="secondary" style={{ fontSize: '11px', marginRight: '4px' }}>
                  {siteStats.total} of {siteStats.originalTotal}
                </Text>
              )
            }
          />
        </div>

        {renderStats()}
        
        <Divider style={{ margin: '12px 0' }} />
        
        <div style={{ 
          maxHeight: siteStats.withContacts > 0 ? '280px' : '350px', 
          overflowY: 'auto' 
        }}>
          <List
            dataSource={filteredSites}
            renderItem={renderSiteItem}
            split={false}
            size="small"
            locale={{
              emptyText: searchTerm ? `No sites found matching "${searchTerm}"` : 'No sites in this area'
            }}
          />
        </div>

        {siteStats.withContacts > 0 && (
          <>
            <Divider style={{ margin: '12px 0' }} />
            <div className="text-center">
              <Button
                type="primary"
                icon={<MessageOutlined />}
                style={{
                  backgroundColor: '#39325b',
                  borderColor: '#39325b'
                }}
                onClick={() => setShowContactModal(true)}
              >
                Send Mass Message ({siteStats.withContacts} contacts)
              </Button>
            </div>
          </>
        )}
      </Card>

      {/* SMS Modal */}
      <Modal
        title={
          <Space>
            <MessageOutlined style={{ color: '#39325b' }} />
            <span>Send Mass SMS Message</span>
          </Space>
        }
        open={showContactModal}
        onCancel={handleModalClose}
        footer={null}
        width={600}
        styles={{ body: { maxHeight: '70vh', overflowY: 'auto' } }}
      >
        <div style={{ marginBottom: '16px' }}>
          <Text type="secondary">
            Sending to {sitesWithContacts.length} site managers within the selected area
          </Text>
        </div>

        {/* Recipients List */}
        <div style={{ marginBottom: '16px', maxHeight: '120px', overflowY: 'auto', backgroundColor: '#f8f9fa', padding: '8px', borderRadius: '6px' }}>
          <Text strong style={{ display: 'block', marginBottom: '8px' }}>Recipients:</Text>
          {sitesWithContacts.map((site, index) => (
            <div key={site.id} style={{ fontSize: '12px', marginBottom: '4px' }}>
              <Text>{site.site_manager_name}</Text>
              <Text type="secondary"> ({site.site_name}) - {site.site_manager_mobile}</Text>
            </div>
          ))}
        </div>

        {/* SMS Templates */}
        <div style={{ marginBottom: '16px' }}>
          <Text strong style={{ display: 'block', marginBottom: '8px' }}>Quick Templates:</Text>
          <Space wrap>
            {SMS_TEMPLATES.map(template => (
              <Button
                key={template.id}
                size="small"
                onClick={() => applyTemplate(template)}
                style={{ marginBottom: '4px' }}
              >
                {template.name}
              </Button>
            ))}
          </Space>
        </div>

        {/* SMS Form */}
        <Form
          form={smsForm}
          layout="vertical"
          onFinish={handleSendSMS}
        >
          <Form.Item
            name="message"
            label={
              <span>
                Message 
                <span style={{ fontSize: '12px', color: '#666', fontWeight: 'normal', marginLeft: '8px' }}>
                  (GSM-7: 160 chars/segment, Multi-segment cost applies)
                </span>
              </span>
            }
            rules={[
              { required: true, message: 'Please enter a message' }
            ]}
          >
            <TextArea
              rows={4}
              placeholder="Enter your emergency message here..."
              showCount

            />
          </Form.Item>

          {/* SMS Segment Indicator */}
          <Form.Item dependencies={['message']} style={{ marginBottom: '16px' }}>
            {({ getFieldValue }) => {
              const currentMessage = getFieldValue('message');
              return (
                <SMSSegmentIndicator 
                  message={currentMessage} 
                  recipientCount={sitesWithContacts.length}
                  showCost={true}
                />
              );
            }}
          </Form.Item>

          <Form.Item style={{ marginBottom: 0 }}>
            <Space>
              <Button
                type="primary"
                htmlType="submit"
                icon={<SendOutlined />}
                loading={sendingSMS}
                style={{ backgroundColor: '#39325b', borderColor: '#39325b' }}
              >
                {sendingSMS ? 'Sending...' : `Send SMS (${sitesWithContacts.length})`}
              </Button>
              <Button onClick={handleModalClose}>
                Cancel
              </Button>
            </Space>
          </Form.Item>
        </Form>

        {/* Results Display */}
        {smsResults.length > 0 && (
          <div style={{ marginTop: '16px', padding: '12px', backgroundColor: '#f8f9fa', borderRadius: '6px' }}>
            <Text strong style={{ display: 'block', marginBottom: '8px' }}>Send Results:</Text>
            <div style={{ maxHeight: '150px', overflowY: 'auto' }}>
              {smsResults.map((result, index) => (
                <div key={index} style={{ marginBottom: '4px', fontSize: '12px' }}>
                  <Space>
                    {result.status === 'success' ? (
                      <Text type="success">✅</Text>
                    ) : (
                      <Text type="danger">❌</Text>
                    )}
                    <Text>{result.manager} ({result.site})</Text>
                    {result.status === 'error' && (
                      <Text type="danger">- {result.error}</Text>
                    )}
                  </Space>
                </div>
              ))}
            </div>
          </div>
        )}

        {sendingSMS && (
          <div style={{ textAlign: 'center', marginTop: '16px' }}>
            <Spin />
            <Text style={{ marginLeft: '8px' }}>Sending SMS messages...</Text>
          </div>
        )}
      </Modal>
    </>
  );
};

export default SiteListPopup; 